import UserProfile from '@zeus/core/UserProfile';
import useOrgAccess from '_/hooks/useOrgAccess';
import { ISource } from '_/types/sources/source.types';
import { compareAssigneeSort, compareSort } from '_/utils/formatUtils';
import { setSourceStatusTag } from '_/utils/orgExtractorUtils';
import { OPS_INCLUDED } from '_/utils/userManagement';
import { Button, Table } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import React, { ReactElement } from 'react';

import ExtractorSourceLink from './ExtractorSourceLink';

interface ExtractorSourcesDrilldownTableProps {
  extractorId: string;
  extractorSources: ISource[];
  handleUpdateSourceExtractorVersion: (source: ISource, versionId: string) => void | Promise<void>;
  isLoading?: boolean;
  latestExtractorVersion?: string;
  loadMoreExtractorSources: (extractorId: string) => void;
}

function ExtractorSourcesDrilldownTable({
  extractorSources,
  handleUpdateSourceExtractorVersion,
  isLoading = false,
  latestExtractorVersion,
}: ExtractorSourcesDrilldownTableProps): ReactElement {
  const access = useOrgAccess(OPS_INCLUDED);

  const columnDefs: ColumnType<ISource>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (_: string, record: ISource) => <ExtractorSourceLink source={record} />,
      sorter: compareSort('name'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text: string) => setSourceStatusTag(text),
    },
    {
      title: 'Assignee',
      dataIndex: 'assigneeId',
      render: (_: string, record: ISource) => <UserProfile tableMode showDetails user={record.assignedUser} />,
      sorter: compareAssigneeSort,
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Version',
      dataIndex: 'extractorVersionId',
      width: 325,
      sorter: compareSort('extractorVersionId'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Action',
      fixed: 'right' as const,
      render: (_: string, record: ISource) =>
        latestExtractorVersion === record.extractorVersionId ? null : (
          <Button
            onClick={() => handleUpdateSourceExtractorVersion(record, latestExtractorVersion!)}
            disabled={!access}
            size="small"
          >
            Use Latest
          </Button>
        ),
    },
  ];

  return (
    <Table
      virtual
      loading={isLoading}
      columns={columnDefs}
      dataSource={extractorSources}
      tableLayout={'fixed'}
      scroll={{ y: 500 }}
      rowKey={(record) => record.id}
      pagination={false}
    />
  );
}

export default ExtractorSourcesDrilldownTable;
